import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';

import {
  useGetSignalFromHints,
  type Rule,
  type Signal,
  type RunesCallout,
} from '@frontend/api';

import { ReqsReviewConfigProvider } from 'modules/rules/ReqsReviewConfigContext';
import { RulesDetailModal } from 'modules/rules/components/RulesDetailModal';
import {
  addIdsToSignal,
  SignalWithId,
} from 'modules/rules/utils/queryBuilding';

export type InitialRulesValues = Partial<Rule> & {
  signals?: Signal[] | SignalWithId[];
};

type RulesDetailModalContextType = {
  isOpen: boolean;
  openModal: (options?: RulesDetailModalOptions) => void;
  closeModal: () => void;
  clearCallout: () => void;
  selectedCalloutMeta: SelectedCalloutMeta | null;
};

type RulesDetailModalOptions = {
  // For editing existing rules
  rule?: Rule;

  // For creating rules from text
  selectedText?: string;

  // For creating rules from callouts
  rawCallout?: RunesCallout;

  // For creating rules from signal API response
  signal?: Signal;

  // Modal behavior
  isReadOnly?: boolean;
  showConfigureRulesButton?: boolean;
  onSuccess?: (rule: Rule) => void;
  onDelete?: (rule: Rule) => void;
  mode?: 'create' | 'update' | 'validate';
  onTriggerSideEffect?: () => void;
};

const RulesDetailModalContext =
  createContext<RulesDetailModalContextType | null>(null);

export const useRulesDetailModalContext = () => {
  const context = useContext(RulesDetailModalContext);
  if (!context) {
    throw new Error(
      'useRulesDetailModalContext must be used within a RulesDetailModalProvider',
    );
  }
  return context;
};

export type SelectedCalloutMeta = {
  description: string | null;
  signal: SignalWithId | null;
};

type RulesDetailModalProviderProps = {
  children: ReactNode;
  onClose?: () => void;
};

export const RulesDetailModalProvider = ({
  children,
  onClose,
}: RulesDetailModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<{
    initialRule?: InitialRulesValues;
    isReadOnly: boolean;
    showConfigureRulesButton?: boolean;
    onSuccess?: (rule: Rule) => void;
    onDelete?: (rule: Rule) => void;
    mode?: 'create' | 'update' | 'validate';
  }>({
    isReadOnly: false,
    mode: 'create',
  });
  const [selectedCalloutMeta, setSelectedCalloutMeta] =
    useState<SelectedCalloutMeta | null>(null);

  const { mutate: getSignalFromHints } = useGetSignalFromHints();

  const openModal = useCallback(
    (options: RulesDetailModalOptions = {}) => {
      let initialRule: InitialRulesValues | undefined;

      if (options.rule) {
        // Editing existing rule
        initialRule = options.rule;
      } else if (options.selectedText) {
        // Creating rule from text - use API to get signal
        getSignalFromHints(
          { rawText: options.selectedText },
          {
            onSuccess: (signal) => {
              const initialRule = { signals: [signal] };
              setModalState({
                initialRule,
                isReadOnly: options.isReadOnly ?? false,
                showConfigureRulesButton: options.showConfigureRulesButton,
                onSuccess: options.onSuccess,
                onDelete: options.onDelete,
                mode: 'create',
              });
              setIsOpen(true);
              options.onTriggerSideEffect?.();
            },
            onError: (error) => {
              console.error('Error fetching signal from text:', error);
            },
          },
        );
        return; // Exit early since the modal will be opened in onSuccess
      } else if (options.rawCallout) {
        // Creating rule from callout - fetch signal first
        getSignalFromHints(
          { rawCallout: options.rawCallout },
          {
            onSuccess: (signal) => {
              const signalWithId = addIdsToSignal(signal);
              setSelectedCalloutMeta({
                description: options.rawCallout?.rawText || null,
                signal: signalWithId,
              });
              setModalState({
                initialRule: { signals: [signalWithId] },
                isReadOnly: options.isReadOnly ?? false,
                showConfigureRulesButton: options.showConfigureRulesButton,
                onSuccess: options.onSuccess,
                onDelete: options.onDelete,
                mode: 'create',
              });
              setIsOpen(true);
              options.onTriggerSideEffect?.();
            },
            onError: (error) => {
              console.error('Error fetching signal from callout:', error);
            },
          },
        );
        return; // Exit early since the modal will be opened in onSuccess
      } else if (options.signal) {
        // Creating rule from provided signal
        initialRule = { signals: [options.signal] };
      }

      // For synchronous cases (rule editing or direct signal)
      setModalState({
        initialRule,
        isReadOnly: options.isReadOnly ?? false,
        showConfigureRulesButton: options.showConfigureRulesButton,
        onSuccess: options.onSuccess,
        onDelete: options.onDelete,
        mode: options.mode ?? 'create',
      });
      setIsOpen(true);
      options.onTriggerSideEffect?.();
    },
    [getSignalFromHints],
  );

  const clearCallout = useCallback(() => {
    setSelectedCalloutMeta(null);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalState({ isReadOnly: false });
    setSelectedCalloutMeta(null);
    onClose?.();
  }, [onClose]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      openModal,
      closeModal,
      clearCallout,
      selectedCalloutMeta,
    }),
    [isOpen, openModal, closeModal, clearCallout, selectedCalloutMeta],
  );

  return (
    <RulesDetailModalContext.Provider value={contextValue}>
      {children}
      <ReqsReviewConfigProvider>
        <RulesDetailModal
          isOpen={isOpen}
          onClose={closeModal}
          initialRule={modalState.initialRule}
          isReadOnly={modalState.isReadOnly}
          showConfigureRulesButton={modalState.showConfigureRulesButton}
          onSuccess={modalState.onSuccess}
          onDeleteRule={modalState.onDelete}
          mode={modalState.mode ?? 'create'}
        />
      </ReqsReviewConfigProvider>
    </RulesDetailModalContext.Provider>
  );
};
